import { CmpEventDataInput } from "../../../graphql/types";
import {
  imageDataUrlToFile,
  optimizeImageFile,
  readFileAsDataUrl,
} from "../../../utils/imageUpload";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  NETWORK_ERROR_TEXT,
  UserFacingError,
} from "../../../utils/userFacingError";

// Leave room for the GraphQL document, headers and API Gateway's base64
// envelope below Lambda's 6 MiB synchronous invocation limit.
const MAX_EVENT_DATA_BYTES = 4 * 1024 * 1024;
const IMAGE_BUDGET_BYTES = 2.5 * 1024 * 1024;
const eventTooLargeMessage =
  "This event is too large to save. Use smaller images or remove images pasted into the event text, then try again.";

const jsonBytes = (value: CmpEventDataInput): number =>
  new Blob([JSON.stringify(value)]).size;

const isInlineImage = (value?: string | null): value is string =>
  Boolean(value?.startsWith("data:"));

/** Prepare both create and update requests without changing the editable draft. */
export const prepareEventDataForSave = async (
  data: CmpEventDataInput,
  options: { imageBudgetBytes?: number } = {},
): Promise<CmpEventDataInput> => {
  const inlineImageCount =
    Number(isInlineImage(data.featuredImageUrl)) +
    (data.speakers || []).filter((speaker) => isInlineImage(speaker.imageUrl)).length +
    (data.partners || []).filter((partner) => isInlineImage(partner.logoUrl)).length;
  const prepared: CmpEventDataInput = {
    ...data,
    speakers: data.speakers?.map((speaker) => ({ ...speaker })) ?? data.speakers,
    partners: data.partners?.map((partner) => ({ ...partner })) ?? data.partners,
  };

  if (inlineImageCount) {
    const maxOutputBytes = Math.floor((options.imageBudgetBytes ?? IMAGE_BUDGET_BYTES) / inlineImageCount);
    const optimize = async (dataUrl: string): Promise<string> => {
      try {
        const file = imageDataUrlToFile({ dataUrl, fileName: "event-image" });
        const output = await optimizeImageFile({
          file,
          outputMimeType: file.type === "image/png" ? "image/png" : "image/jpeg",
          maxOutputBytes,
          targetSizeMB: Math.min(1, maxOutputBytes / (1024 * 1024)),
        });
        return await readFileAsDataUrl(output);
      } catch {
        throw new UserFacingError(
          "An event image could not be prepared for saving. Use a smaller PNG or JPG image, then try again."
        );
      }
    };
    if (isInlineImage(prepared.featuredImageUrl)) {
      prepared.featuredImageUrl = await optimize(prepared.featuredImageUrl);
    }
    // Process one image at a time to avoid decoding all full-size images at once.
    for (const speaker of prepared.speakers || []) {
      if (isInlineImage(speaker.imageUrl)) {
        speaker.imageUrl = await optimize(speaker.imageUrl);
      }
    }
    for (const partner of prepared.partners || []) {
      if (isInlineImage(partner.logoUrl)) {
        partner.logoUrl = await optimize(partner.logoUrl);
      }
    }
  }

  if (jsonBytes(prepared) > MAX_EVENT_DATA_BYTES) {
    throw new UserFacingError(eventTooLargeMessage);
  }
  return prepared;
};

/**
 * Staff text for a failed event save. A network failure has an unknown
 * outcome, so staff check Manage Events before they save again.
 */
export const getEventSaveErrorMessage = (
  error: unknown,
  fallback: string = buildStaffErrorFallback("save the event"),
  /** Update Event: the event is already saved, so staff check their changes. */
  { eventExists = false }: { eventExists?: boolean } = {}
): string => {
  const text = getUserFacingErrorText(error, fallback);
  if (text === NETWORK_ERROR_TEXT) {
    return eventExists
      ? "We could not reach Chamber Member Pro. Your entries are still on this form. Check your internet connection. Then check Manage Events to see if your changes were saved before you try again."
      : "We could not reach Chamber Member Pro. Your entries are still on this form. Check whether the event appears in Manage Events before you try again.";
  }
  return text;
};
